import React, { useState, useEffect } from 'react';
import { notificationsAPI } from '../services/api';

function Notifications() {
  const [formData, setFormData] = useState({
    title: '',
    message: '',
    target_audience: 'all',
    user_ids: '',
    delivery_method: 'push',
    priority: 'normal',
  });
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });
  const [history, setHistory] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    setLoadingHistory(true);
    try {
      const response = await notificationsAPI.getNotifications({ limit: 10 });
      setHistory(response.data.data || []);
    } catch (error) {
      console.error('Error fetching history:', error);
    } finally {
      setLoadingHistory(false);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.title.trim() || !formData.message.trim()) {
      setMessage({ type: 'error', text: 'Title and message are required' });
      return;
    }

    setSending(true);
    setMessage({ type: '', text: '' });

    try {
      const payload = {
        title: formData.title,
        message: formData.message,
        target_audience: formData.target_audience,
        delivery_method: formData.delivery_method,
        priority: formData.priority,
      };

      if (formData.target_audience === 'specific' && formData.user_ids) {
        payload.user_ids = formData.user_ids.split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
      }

      const response = await notificationsAPI.sendNotification(payload);
      
      setMessage({ 
        type: 'success', 
        text: `Notification sent successfully to ${response.data.sent_count} users!` 
      });
      
      // Reset form
      setFormData({
        title: '',
        message: '',
        target_audience: 'all',
        user_ids: '',
        delivery_method: 'push',
        priority: 'normal',
      });

      // Refresh history
      fetchHistory();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Failed to send notification' 
      });
    } finally {
      setSending(false);
    }
  };

  return (
    <div>
      <div className="page-header">
        <h1> 알림 보내기 </h1>
        <p> 사용자에게 알림 또는 이메일 보내기</p>
      </div>

      {message.text && (
        <div className={message.type === 'success' ? 'success' : 'error'} style={{ marginBottom: '20px' }}>
          {message.text}
        </div>
      )}

      <div className="card">
        <div className="card-header">
          <h2>Create Notification</h2>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              name="title"
              className="form-control"
              placeholder="Notification title"
              value={formData.title}
              onChange={handleInputChange}
              required
            />
          </div>

          <div className="form-group">
            <label>Message *</label>
            <textarea
              name="message"
              className="form-control"
              placeholder="Notification message"
              value={formData.message}
              onChange={handleInputChange}
              rows="4"
              required
            />
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
            <div className="form-group">
              <label>Target Audience</label>
              <select
                name="target_audience"
                className="form-control"
                value={formData.target_audience}
                onChange={handleInputChange}
              >
                <option value="all">All Users</option>
                <option value="specific">Specific Users</option>
                <option value="new_users">New Users (Last 30 Days)</option>
                <option value="active_users">Active Users (Last 7 Days)</option>
              </select>
            </div>

            <div className="form-group">
              <label>Delivery Method</label>
              <select
                name="delivery_method"
                className="form-control"
                value={formData.delivery_method}
                onChange={handleInputChange}
              >
                <option value="push">Push Notification</option>
                <option value="email">Email</option>
                <option value="both">Both</option>
              </select>
            </div>
          </div>

          {formData.target_audience === 'specific' && (
            <div className="form-group">
              <label>User IDs (comma-separated)</label>
              <input
                type="text"
                name="user_ids"
                className="form-control"
                placeholder="e.g., 1, 2, 3, 4"
                value={formData.user_ids}
                onChange={handleInputChange}
              />
            </div>
          )}

          <div className="form-group">
            <label>Priority</label>
            <select
              name="priority"
              className="form-control"
              value={formData.priority}
              onChange={handleInputChange}
            >
              <option value="low">Low</option>
              <option value="normal">Normal</option>
              <option value="high">High</option>
            </select>
          </div>

          <button type="submit" className="btn btn-success" disabled={sending}>
            {sending ? 'Sending...' : 'Send Notification'}
          </button>
        </form>
      </div>

      <div className="card">
        <div className="card-header">
          <h2>Notification History</h2>
        </div>
        
        {loadingHistory ? (
          <div className="loading">Loading history...</div>
        ) : history.length === 0 ? (
          <div style={{ textAlign: 'center', padding: '40px', color: '#7f8c8d' }}>
            No notifications sent yet
          </div>
        ) : (
          <div className="table-container">
            <table>
              <thead>
                <tr>
                  <th>Title</th>
                  <th>Method</th>
                  <th>Priority</th>
                  <th>Sent</th>
                  <th>Read</th>
                  <th>Read Rate</th>
                  <th>Last Sent</th>
                </tr>
              </thead>
              <tbody>
                {history.map((item, index) => (
                  <tr key={index}>
                    <td>{item.title}</td>
                    <td>
                      <span className="badge badge-info">
                        {item.delivery_method}
                      </span>
                    </td>
                    <td>
                      <span className={`badge ${
                        item.priority === 'high' ? 'badge-danger' : 
                        item.priority === 'normal' ? 'badge-success' : 'badge-warning'
                      }`}>
                        {item.priority}
                      </span>
                    </td>
                    <td>{item.sent_count}</td>
                    <td>{item.read_count}</td>
                    <td>{item.read_rate}%</td>
                    <td>{new Date(item.last_sent_at).toLocaleString()}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default Notifications;